/**
 * Chat query operations - Get
 */

import type { UserId } from '../../types';
import { getSupabaseClient, executeQueryNullable, executeQueryArray } from '../../supabase-client';
import type { StoredChat, QueryOptions, QueryResult } from '../types';
import { logger } from '../../../logger';

/**
 * Get a single chat by ID
 */
export async function getChatById(chatId: string): Promise<StoredChat | null> {
  try {
    const client = getSupabaseClient();
    const { data, error } = await client
      .from('meridian_chats')
      .select('*')
      .eq('id', chatId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows found
        return null;
      }
      throw error;
    }

    return data as StoredChat;
  } catch (error) {
    logger.error('error getting chat by id', error, { op: 'getChatById' });
    throw error;
  }
}

/**
 * Get all chats for a user
 */
export async function getChatsByUser(
  userId: UserId,
  status?: 'active' | 'archived' | 'deleted',
  options?: QueryOptions
): Promise<QueryResult<StoredChat>> {
  try {
    const client = getSupabaseClient();
    const limit = options?.limit || 50;
    const offset = options?.offset || 0;
    const ascending = options?.ascending ?? false;

    let query = client
      .from('meridian_chats')
      .select('*', { count: 'exact' })
      .eq('user_id', userId);

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error, count } = await query
      .order('created_at', { ascending })
      .range(offset, offset + limit - 1);

    if (error) {
      throw error;
    }

    const items = (data || []) as StoredChat[];
    const hasMore = count ? offset + limit < count : false;

    return {
      items,
      hasMore,
      offset,
    };
  } catch (error) {
    logger.error('error getting chats by user', error, { op: 'getChatsByUser' });
    throw error;
  }
}

/**
 * Get chats created after a specific date
 */
export async function getChatsByCreatedAfter(
  createdAfter: string,
  options?: QueryOptions
): Promise<QueryResult<StoredChat>> {
  try {
    const client = getSupabaseClient();
    const limit = options?.limit || 50;
    const offset = options?.offset || 0;
    const ascending = options?.ascending ?? false;

    const { data, error, count } = await client
      .from('meridian_chats')
      .select('*', { count: 'exact' })
      .gte('created_at', createdAfter)
      .order('created_at', { ascending })
      .range(offset, offset + limit - 1);

    if (error) {
      throw error;
    }

    const items = (data || []) as StoredChat[];
    const hasMore = count ? offset + limit < count : false;

    return {
      items,
      hasMore,
      offset,
    };
  } catch (error) {
    logger.error('error getting chats by created after', error, { op: 'getChatsByCreatedAfter' });
    throw error;
  }
}
